import type { Scenario } from '../types';

export const PRESSURE_POINT: Scenario = {
  id: 'pressure-point',
  title: 'the pressure point',
  brief: 'two weeks in. he suggests something you have been curious about but have not committed to. the pressure is soft.',
  skill: 'recognizing gentle pressure and asking for time without being apologetic about it',
  estMinutes: 5,
  startNode: 'open',
  nodes: {
    open: {
      id: 'open',
      paragraphs: [
        { text: 'you are in his kitchen. dinner has been finished long enough that the plates are stacked on the counter and neither of you has gotten up to deal with them.', speaker: 'narration' },
        { text: '"i\'ve been thinking about something," he says. "the thing you mentioned wanting to try. i think we should this weekend."', speaker: 'them' },
        { text: 'it is something you named, weeks ago, as a curiosity. you did not commit. your body feels both warmer and tighter at the same time.', speaker: 'narration' },
      ],
      choices: [
        { text: 'tell him you need to think about it.', nextNode: 'time-ask' },
        { text: 'ask him what specifically he has in mind.', nextNode: 'specifics' },
        { text: 'agree right now — say yes.', nextNode: 'fast-yes' },
        { text: 'remind him you said curious, not yes.', nextNode: 'remind' },
      ],
    },
    'time-ask': {
      id: 'time-ask',
      paragraphs: [
        { text: 'you say it plainly. no apology.', speaker: 'narration' },
        { text: 'he pauses, considering. then: "of course. how much time do you need?"', speaker: 'them' },
      ],
      choices: [
        { text: 'name a specific timeframe. "a week."', nextNode: 'specific-time' },
        { text: '"i\'ll let you know when i know." don\'t commit to a deadline.', nextNode: 'open-time' },
      ],
    },
    specifics: {
      id: 'specifics',
      paragraphs: [
        { text: 'he describes it. specifically. with the kind of detail that suggests he has imagined it in some detail.', speaker: 'narration' },
        { text: 'you notice something — the version he describes is denser than the one you were curious about. you are not sure he noticed the difference.', speaker: 'narration' },
      ],
      choices: [
        { text: 'name the gap. "what i was curious about was a lighter version of that."', nextNode: 'gap-named' },
        { text: 'agree, hoping the lighter version will emerge in practice.', nextNode: 'hopeful-yes' },
        { text: 'ask for time, now that you know what the specific version is.', nextNode: 'time-ask' },
      ],
    },
    'fast-yes': {
      id: 'fast-yes',
      paragraphs: [
        { text: 'the word comes out before you have decided. you watch him hear it.', speaker: 'narration' },
        { text: '"you sure?" he asks. "you can take more time."', speaker: 'them' },
        { text: 'his question is the thing you wish you had given yourself.', speaker: 'narration' },
      ],
      choices: [
        { text: 'use the door he just opened. "actually, let me think about it."', nextNode: 'time-ask' },
        { text: 'commit. "yes. i\'m sure."', nextNode: 'committed' },
      ],
    },
    remind: {
      id: 'remind',
      paragraphs: [
        { text: '"i remember," he says. "but i thought maybe by now you\'d know more about which way you were leaning."', speaker: 'them' },
        { text: 'it lands somewhere between observation and pressure. you cannot quite tell which.', speaker: 'narration' },
      ],
      choices: [
        { text: 'name the texture: "that feels like a soft push."', nextNode: 'push-named' },
        { text: 'tell him you still need more time.', nextNode: 'time-ask' },
        { text: 'agree to it now.', nextNode: 'fast-yes' },
      ],
    },
    'specific-time': {
      id: 'specific-time',
      paragraphs: [
        { text: '"a week. okay."', speaker: 'them' },
        { text: 'he changes the subject as if he had asked you whether you wanted dessert. you do not feel like he is holding the question over the rest of the evening.', speaker: 'narration' },
      ],
      choices: [
        { text: 'finish the evening as if the conversation hadn\'t happened.', nextNode: 'close' },
      ],
    },
    'open-time': {
      id: 'open-time',
      paragraphs: [
        { text: 'he nods. it is a slower nod than the one you might have wanted, but he does not push.', speaker: 'narration' },
        { text: '"okay."', speaker: 'them' },
        { text: 'the topic is closed. for tonight.', speaker: 'narration' },
      ],
      choices: [
        { text: 'finish the evening.', nextNode: 'close' },
      ],
    },
    'gap-named': {
      id: 'gap-named',
      paragraphs: [
        { text: 'he sits with it for a moment.', speaker: 'narration' },
        { text: '"thank you. you\'re right. i wasn\'t making the distinction. would you want to do the version you were curious about, or do you want to not do anything in this category yet?"', speaker: 'them' },
      ],
      choices: [
        { text: '"the lighter version. and on a timeline i choose."', nextNode: 'agreed' },
        { text: '"nothing yet. i need to sit with this more."', nextNode: 'open-time' },
      ],
    },
    'hopeful-yes': {
      id: 'hopeful-yes',
      paragraphs: [
        { text: 'you say yes. the hope is silent — that what he meant and what you wanted will end up being closer than they sounded.', speaker: 'narration' },
        { text: 'in your stomach there is a small grain of having not advocated for yourself.', speaker: 'narration' },
      ],
      choices: [
        { text: 'name the grain now, before the evening ends.', nextNode: 'rescue' },
        { text: 'let it sit. trust that it will resolve.', nextNode: 'committed' },
      ],
    },
    'push-named': {
      id: 'push-named',
      paragraphs: [
        { text: 'he hears it. you watch him register it as feedback, not as accusation.', speaker: 'narration' },
        { text: '"that\'s fair," he says. "i was nudging. i shouldn\'t have. take the time."', speaker: 'them' },
      ],
      choices: [
        { text: 'thank him for hearing it. let the topic close for tonight.', nextNode: 'close' },
      ],
    },
    rescue: {
      id: 'rescue',
      paragraphs: [
        { text: '"actually — i want to walk that back. i think i said yes faster than i meant to."', speaker: 'you' },
        { text: 'he sets down his glass.', speaker: 'narration' },
        { text: '"thank you. seriously. take the time you need."', speaker: 'them' },
      ],
      choices: [
        { text: 'close the topic.', nextNode: 'close' },
      ],
    },
    committed: {
      id: 'committed',
      paragraphs: [
        { text: 'the rest of the evening is pleasant. underneath, you notice that you are negotiating with yourself about something that should have been negotiated with him.', speaker: 'narration' },
        { text: 'tomorrow, you will probably want to bring it up.', speaker: 'narration' },
      ],
    },
    agreed: {
      id: 'agreed',
      paragraphs: [
        { text: '"good," he says. "tell me when you want to."', speaker: 'them' },
        { text: 'you change the subject yourself. it feels good to be the one who closed the conversation.', speaker: 'narration' },
      ],
    },
    close: {
      id: 'close',
      paragraphs: [
        { text: 'later, after he has gone, you replay the conversation once. you find no place where you owed him a yes you didn\'t give. that is a good measurement.', speaker: 'narration' },
      ],
    },
  },
  reflection: {
    title: 'on pressure that doesn\'t feel like pressure',
    skillPracticed: 'naming the texture of a soft nudge and asking for time without performing an apology',
    body: 'soft pressure is hardest to catch because it does not look like pressure — it looks like enthusiasm, or shared excitement, or someone who has been thinking. the test is your own body: did you notice yourself rushing toward a yes you had not decided on? did you feel the shape of something you needed to advocate for? asking for time is not difficult because the other person resists; it is difficult because asking for time names the thing you are asking time for. that naming is the skill.',
    otherPaths: [
      'you could have asked him what he would do if you said no permanently to this specific thing. his answer tells you a lot.',
      'you could have made a counter-offer — a smaller version, on a different timeline. that is its own form of yes that protects your pace.',
      'you could have asked yourself, before any of his suggestions, what you actually want this week. answering for yourself first changes the whole conversation.',
    ],
  },
};
